import type { IFormattedLoan } from 'store/fetchers/loans/enum'
import type { UniversalCardProps } from 'pages/account/components/deposit-card/universal-card/types'

import { useEffect, useState, FC, useMemo } from 'react'
import { useRouter } from 'next/router'

import { updateInitialDeposit } from 'store/fetchers/loans'
import { useAppDispatch } from 'store/hooks'
import { fetchRates } from 'store/reducers/rates'

import { formatNumber } from 'utils/formatters'
import { useMultipleCurrency } from 'utils/use-currency'

import { UniversalCard } from 'pages/account/components/deposit-card/universal-card'
import { getFormattedDate, getFormattedTime } from 'pages/account/components/deposit-card/utils'

import Skeleton from '../../../../../components/ui/skeleton'

import { roundNumberWithPrecision } from '../../../../../utils/round-number'

import { countDecimalPlaces } from '../../../../../utils/count-decimal-places'

import { floorNumber } from '../../../../../utils/floor-number'

import styles from './styles.module.scss'

import { getFormattedLoanStatus, getLoanCardColumns } from './utils'

interface LoanCardProps {
  key: string
  loan: IFormattedLoan
}

export const LoanCard: FC<LoanCardProps> = ({ loan }) => {
  const router = useRouter()
  const dispatch = useAppDispatch()

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [rate, setRate] = useState(null)
  const [from, to] = useMultipleCurrency([
    {
      code: loan?.deposit?.currency_code,
      network: loan?.deposit?.currency_network,
    },
    {
      code: loan?.loan?.currency_code,
      network: loan?.loan?.currency_network,
    },
  ])

  useEffect(() => {
    if (!loan) return

    async function fetcher() {
      try {
        const rates = await dispatch(
          fetchRates({
            baseCode: loan?.deposit?.currency_code?.toUpperCase(),
            targetCode: loan?.loan?.currency_code?.toUpperCase(),
            base: from,
            target: to,
            from: new Date(loan.created_at).getTime() - 1000 * 60 * 60 * 24 * 5,
          }),
        )

        setRate(rates[rates.length - 1]?.rate)
      } catch (error) {} finally {
        setIsLoading(false)
      }
    }

    fetcher()
  }, [from, to, loan])

  const trackLoan = async () => {
    const statuses = ['new', 'confirmed', 'deposit_received', 'transaction_sent']

    if (statuses.includes(loan.status)) {
      if (!loan.deposit.active) {
        await updateInitialDeposit({ loan_id: loan.loan_id })
      }

      if (loan.receive_deposit_from === 'external_wallet' || loan.deposit.receive_from !== 'internal_wallet') {
        await router.push(`/account/loans/send?loan_id=${loan.loan_id}`)
      } else {
        await router.push(`/account/loans/success?loan_id=${loan.loan_id}`)
      }
    } else {
      await router.push(`/account/loans/info?loan_id=${loan.loan_id}`)
    }
  }

  const formattedLoanStatus = getFormattedLoanStatus(loan.status)
  const cardInfo: UniversalCardProps = useMemo(
    () => ({
      columns: getLoanCardColumns(loan),
      onTrackClick: trackLoan,
      amountCell: {
        amount: formatNumber(
          roundNumberWithPrecision(+loan?.deposit?.amount || +loan?.deposit?.expected_amount, 6),
        ).toString(),
        additionalAmount: `$${formatNumber(
          floorNumber(+(loan?.deposit?.amount || loan?.deposit?.expected_amount) * +loan?.deposit?.usdt_rate, 2),
        )}`,
        ticker: loan?.deposit?.currency_code?.toUpperCase(),
        network: loan?.deposit?.currency_network?.toUpperCase(),
        className: styles.amount,
      },
      regularCell: [
        {
          mainText: getFormattedDate(loan.created_at),
          secondaryText: getFormattedTime(loan.created_at),
          date: true,
          key: 'date',
        },
        {
          mainText: rate
            ? countDecimalPlaces(rate) > 6
              ? formatNumber(+rate?.toFixed(from?.decimal_places))
              : formatNumber(+rate)
            : '-',
          secondaryText: `${loan?.deposit?.currency_code?.toUpperCase()}/${loan?.loan?.currency_code?.toUpperCase()}`,
          key: 'depositPrice',
        },
        {
          mainText: loan?.liquidation_price
            ? countDecimalPlaces(+loan?.liquidation_price) > 6
              ? formatNumber(Number(loan.liquidation_price).toFixed(6))
              : formatNumber(loan.liquidation_price)
            : '-',
          secondaryText: `${loan?.deposit?.currency_code?.toUpperCase()}/${loan?.loan?.currency_code?.toUpperCase()}`,
          key: 'marginCall',
        },
      ],
      statusCell: {
        status: formattedLoanStatus,
        id: loan?.loan_id,
        createdAt: formattedLoanStatus === 'Pending' ? loan?.created_at : undefined,
        isLoans: true,
      },
    }),
    [trackLoan, from, to, rate],
  )

  return isLoading ? (
    <Skeleton className={styles.skeleton} />
  ) : (
    <UniversalCard
      loan={loan}
      onTrackClick={cardInfo.onTrackClick}
      columns={cardInfo.columns}
      amountCell={cardInfo.amountCell}
      regularCell={cardInfo.regularCell}
      statusCell={cardInfo.statusCell}
    />
  )
}
